import { apiRequest } from '../request'
import type { ApiResponse } from '../request'

export type HelpTicketStatus =
    | 'OPEN'
    | 'IN_PROGRESS'
    | 'ANSWERED'
    | 'RESOLVED'
    | 'CLOSED'

export type HelpTicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'

export interface HelpTicketMessage {
    id: number
    ticketId: number
    content: string
    authorUserId: number
    authorName: string
    isAgent: boolean
    createdAt: string
}

export interface HelpTicket {
    id: number
    title: string
    status: HelpTicketStatus
    priority: HelpTicketPriority
    authorUserId: number
    authorName: string
    authorEmail?: string | null
    organizationId?: number | null
    companyId?: number | null
    lastReplyAt?: string | null
    createdAt: string
    updatedAt: string
    messages?: HelpTicketMessage[]
    _count?: { messages: number }
}

export interface HelpArticle {
    id: number
    title: string
    content: string
    moduleKey?: string | null
    resourceKey?: string | null
    authorUserId: number
    authorName: string
    published: boolean
    createdAt: string
    updatedAt: string
}

export interface HelpCenterCapabilities {
    isAgent: boolean
    canReadAllTickets: boolean
    canManageTickets: boolean
    canCreateArticles: boolean
    canUpdateArticles: boolean
    canDeleteArticles: boolean
}

export interface PaginatedTickets {
    items: HelpTicket[]
    total: number
    page: number
    limit: number
}

// ===== Capacidades =====
export async function getHelpCenterCapabilities(): Promise<
    ApiResponse<HelpCenterCapabilities>
> {
    return apiRequest<HelpCenterCapabilities>('help-center/me', {})
}

// ===== Upload de imagem (editor) =====
export async function uploadHelpImage(
    file: File,
): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    return apiRequest<{ url: string }>(
        'help-center/upload-image',
        { data: formData, formData: true },
        'post',
    )
}

// ===== Tickets =====
export async function createTicket(data: {
    title: string
    content: string
    priority?: HelpTicketPriority
}): Promise<ApiResponse<HelpTicket>> {
    return apiRequest<HelpTicket>('help-center/tickets', { data }, 'post')
}

export async function getMyTickets(): Promise<ApiResponse<HelpTicket[]>> {
    return apiRequest<HelpTicket[]>('help-center/tickets/mine', {})
}

export async function getAllTickets(params?: {
    status?: HelpTicketStatus
    search?: string
    page?: number
    limit?: number
}): Promise<ApiResponse<PaginatedTickets>> {
    return apiRequest<PaginatedTickets>('help-center/tickets', { params })
}

export async function getTicket(id: number): Promise<ApiResponse<HelpTicket>> {
    return apiRequest<HelpTicket>(`help-center/tickets/${id}`, {})
}

export async function replyTicket(
    id: number,
    content: string,
): Promise<ApiResponse<HelpTicket>> {
    return apiRequest<HelpTicket>(
        `help-center/tickets/${id}/messages`,
        { data: { content } },
        'post',
    )
}

export async function updateTicket(
    id: number,
    data: { status?: HelpTicketStatus; priority?: HelpTicketPriority },
): Promise<ApiResponse<HelpTicket>> {
    return apiRequest<HelpTicket>(`help-center/tickets/${id}`, { data }, 'patch')
}

export async function deleteTicket(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`help-center/tickets/${id}`, {}, 'delete')
}

// ===== Artigos =====
export async function getArticles(params?: {
    search?: string
    moduleKey?: string
    resourceKey?: string
    includeUnpublished?: boolean
}): Promise<ApiResponse<HelpArticle[]>> {
    return apiRequest<HelpArticle[]>('help-center/articles', { params })
}

export async function getArticle(id: number): Promise<ApiResponse<HelpArticle>> {
    return apiRequest<HelpArticle>(`help-center/articles/${id}`, {})
}

export async function createArticle(data: {
    title: string
    content: string
    moduleKey?: string
    resourceKey?: string
    published?: boolean
}): Promise<ApiResponse<HelpArticle>> {
    return apiRequest<HelpArticle>('help-center/articles', { data }, 'post')
}

export async function updateArticle(
    id: number,
    data: {
        title?: string
        content?: string
        moduleKey?: string
        resourceKey?: string
        published?: boolean
    },
): Promise<ApiResponse<HelpArticle>> {
    return apiRequest<HelpArticle>(`help-center/articles/${id}`, { data }, 'patch')
}

export async function deleteArticle(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`help-center/articles/${id}`, {}, 'delete')
}
